import React       from 'react';
import Select      from 'react-select';
import styled, {
  css,
  FlattenSimpleInterpolation,
}                  from 'styled-components';
import CheckIcon   from '@material-ui/icons/Check';
import { IOption } from '@typings/components';

interface IStyledDivProps {
  isOption?     : boolean;
  isFocused?    : boolean;
  isWhiteColor? : boolean;
}

export interface IDropdownProps {
  width?               : number;
  backgroundColor?     : string;
  defaultValue         : any;
  placeholder          : string;
  disabled?            : boolean;
  options              : IOption[];
  field                : any;
  form?                : any;
  textAlign?           : string;
  customOnValueChange? : (value: any) => void;
}

const SelectStyled = styled(Select)`
  max-height : 35px;
  cursor     : pointer;
  width      : ${({ width }: any): string => (width ? `${width}px` : '100%')};
  ${({ textAlign }: any): string => `text-align: ${textAlign}`};

  [class*='-placeholder'] {
    white-space: nowrap;
  }

  [class*='-control'] {
    > div:first-child {
      flex-wrap: nowrap;

      ${({ theme }): false | string => theme.width !== 'xs' && `
        max-height: 35px;
      `}

      > div {
        padding-left  : 4px;
        width         : 175px;
        white-space   : nowrap;
        overflow      : hidden;
        text-overflow : ellipsis;
      }
    }
  }

  > div:first-child {
    > div:first-child {
      ${({ theme }): false | string => theme.width !== 'xs' && `
        max-height: 35px;
      `}
    }
  }
`;

const OptionContainer = styled.div`
  color            : ${({ isWhiteColor } : IStyledDivProps) : string => (isWhiteColor ? '#fff' : '#000')};
  background-color : ${({ isFocused }    : IStyledDivProps) : string => (isFocused ? '#EBEBEB' : '')};
  padding          : 8px 0 8px 12px;
  position         : relative;

  ${({ theme }): false | FlattenSimpleInterpolation => theme.width !== 'xs' && css`
    white-space: nowrap;
  `}
`;

const MultipleOptionContainer = styled.div`
  display         : flex;
  justify-content : space-between;

 ${({ isOption } : IStyledDivProps) : FlattenSimpleInterpolation =>
    (isOption
      ? css`
         width        : calc(100% - 42px);
         margin-right : 42px;
       `
      : css`
         width : 100%;
       `
    )}
`;

const MultipleOptionContent = styled.div`
  white-space   : nowrap;
  overflow      : hidden;
  text-overflow : ellipsis;
`;

const Divider = styled.div`
  width         : 97%;
  position      : absolute;
  top           : -8px;
  border-bottom : 1px solid black;
`;

const CheckIconWrapper = styled.div`
  position : absolute;
  right    : 5px;
  top      : 6px;
`;

export interface ICustomOption {
  type        : string;
  data        : IOption;
  setValue    : (arg: null) => void;
  isFocused   : boolean;
  innerProps  : any;
  selectProps : any;
}

const CustomOption: any = ({
  type,
  data,
  setValue,
  isFocused,
  innerProps,
  selectProps,
}: ICustomOption): JSX.Element => {
  const isOption     = type === 'option';
  const isWhiteColor = !isOption && selectProps.backgroundColor !== 'white';
  const onClick      = data.withAction ? data.onClick : (innerProps && innerProps.onClick);
  const { label }    = data;

  const displayedLabel : string | JSX.Element = typeof label === 'string'
    ? label
    : (
      <MultipleOptionContainer isOption={isOption}>
        <MultipleOptionContent>{label.status}</MultipleOptionContent>
        {label.count}
      </MultipleOptionContainer>
    );

  CustomOption.updatedOnClick = (arg: any): void => {
    if (data.withAction) {
      setValue(null);
    }

    if (onClick) {
      onClick(arg);
    }
  };

  return (
    <OptionContainer
      {...innerProps}
      onClick      = {CustomOption.updatedOnClick}
      isWhiteColor = {isWhiteColor}
      isFocused    = {isFocused}
    >
      {data.withAction && <Divider />}
      {displayedLabel}
      {isOption && ((selectProps?.value?.value ?? '') === data.value) && (
        <CheckIconWrapper>
          <CheckIcon />
        </CheckIconWrapper>
      )}
    </OptionContainer>
  );
};

export class Dropdown extends React.Component<IDropdownProps> {
  static defaultProps: any; // eslint-disable-line react/static-property-placement

  onValueChange = (value: IOption): void => {
    const { field, form } = this.props;

    if (value) {
      form.setFieldValue(field.name, value.value);
      form.setFieldTouched(field.name, value.value);
    }
  }

  getValue = (): any => {
    const { field: { value }, options } = this.props;

    const withAction = options.some(
      (option: any): boolean => option.withAction,
    );

    if (!withAction) {
      return options
        .filter((option: IOption): boolean => option.value === value)[0];
    }

    const optionsWithoutAction = options.filter(
      (option: IOption): boolean => !option.withAction,
    );

    return optionsWithoutAction[0] && optionsWithoutAction[0].options
      ? optionsWithoutAction[0].options
        .filter((option: IOption): boolean => option.value === value)[0]
      : null;
  }

  render(): JSX.Element {
    const {
      width,
      field,
      options,
      disabled,
      textAlign,
      placeholder,
      defaultValue,
      backgroundColor,
      customOnValueChange,
    } = this.props;

    const fontColor = backgroundColor === 'white' ? '#000' : '#fff';

    return (
      <SelectStyled
        {...field}
        value                 = {this.getValue()}
        backgroundColor       = {backgroundColor}
        defaultValue          = {options.filter(
          (option: IOption): boolean => option.value === defaultValue,
        )}
        backspaceRemovesValue = {false}
        isSearchable          = {false}
        components            = {{
          Option      : CustomOption,
          SingleValue : CustomOption,
        }}
        options               = {options}
        placeholder           = {placeholder}
        width                 = {width}
        textAlign             = {textAlign}
        onChange              = {
          (value: IOption): void => (
            customOnValueChange ? customOnValueChange(value) : this.onValueChange(value)
          )
        }
        styles                = {{
          control: (base: any): object => ({
            ...base,
            backgroundColor,
            border      : '1px solid #ddd',
            boxShadow   : 'unset',
            borderWidth : 'unset',
            cursor      : 'pointer',
          }),
          menu: (base: any): object => ({
            ...base,
            position     : 'absolute',
            top          : '40px',
            zIndex       : '9999',
            textAlign    : 'left',
            fontWeight   : '300',
            marginBottom : '50px',
          }),
          indicatorSeparator : (): null => null,
          dropdownIndicator  : (base: any): object => ({ ...base, color: fontColor }),
          singleValue        : (base: any): object => ({ ...base, fontWeight: 500 }),
          placeholder        : (base: any): object => ({ ...base, color: fontColor }),
        }}
        isDisabled            = {disabled ?? false}
      />
    );
  }
}

Dropdown.defaultProps = {
  options         : [],
  backgroundColor : 'white',
  defaultValue    : null,
  placeholder     : 'Select Action',
};
